import { Search, Filter, Clock, FileText, User, Loader2, Building2, FolderOpen } from 'lucide-react'
import { useState, useCallback, useEffect } from 'react'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useKnowledgeBaseSearch,
  useKnowledgeBases,
  type SearchResult,
  type SearchFilters,
  type KnowledgeBaseType,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface SearchPanelProps {
  onSelectResult?: (documentId: string) => void
  className?: string
}

/**
 * 搜索面板组件
 * 支持全文搜索、筛选和最近搜索
 */
export function SearchPanel({ onSelectResult, className }: SearchPanelProps) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [filters, setFilters] = useState<SearchFilters>({})
  const [showFilters, setShowFilters] = useState(false)
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    const saved = localStorage.getItem('kb-recent-searches')
    return saved ? (JSON.parse(saved) as string[]) : []
  })

  const { data: knowledgeBases } = useKnowledgeBases()
  const { data: results, isLoading, isFetching } = useKnowledgeBaseSearch(debouncedQuery, filters)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  // Save to recent searches
  const saveRecentSearch = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) { return }
    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s !== searchQuery)
      const updated = [searchQuery, ...filtered].slice(0, 5)
      localStorage.setItem('kb-recent-searches', JSON.stringify(updated))
      return updated
    })
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      saveRecentSearch(query.trim())
    }
  }

  const handleSelectResult = (result: SearchResult) => {
    saveRecentSearch(query.trim())
    onSelectResult?.(result.id)
  }

  const clearRecentSearches = () => {
    setRecentSearches([])
    localStorage.removeItem('kb-recent-searches')
  }

  // Separate project knowledge bases
  const projectKBs = knowledgeBases?.filter((kb) => kb.type === 'project') ?? []

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Search Input */}
      <div className="border-b p-4">
        <form onSubmit={handleSearch}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索文档..."
              className="w-full rounded-md border bg-transparent py-2 pl-10 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            {(isLoading || isFetching) && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
            )}
          </div>
        </form>

        {/* Filter Toggle */}
        <div className="mt-2 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="text-muted-foreground"
          >
            <Filter className="mr-2 h-4 w-4" />
            筛选
          </Button>
          {Object.keys(filters).length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({})}
              className="text-muted-foreground"
            >
              清除筛选
            </Button>
          )}
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">知识库类型</label>
              <Select
                value={filters.type ?? 'all'}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    type: value === 'all' ? undefined : (value as KnowledgeBaseType),
                    projectId: value === 'company' ? undefined : prev.projectId,
                  }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="全部" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部</SelectItem>
                  <SelectItem value="company">公司知识库</SelectItem>
                  <SelectItem value="project">项目知识库</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {filters.type === 'project' && projectKBs.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground">项目</label>
                <Select
                  value={filters.projectId ?? 'all'}
                  onValueChange={(value) =>
                    setFilters((prev) => ({
                      ...prev,
                      projectId: value === 'all' ? undefined : value,
                    }))
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="全部项目" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部项目</SelectItem>
                    {projectKBs.map((kb) => (
                      <SelectItem key={kb.projectId} value={kb.projectId ?? ''}>
                        {kb.project?.name ?? kb.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground">时间范围</label>
              <Select
                value={filters.startDate ? 'custom' : 'all'}
                onValueChange={(value) => {
                  const now = new Date()
                  let startDate: string | undefined
                  if (value === 'week') {
                    startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                  } else if (value === 'month') {
                    startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                  } else if (value === 'year') {
                    startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                  }
                  setFilters((prev) => ({
                    ...prev,
                    startDate,
                    endDate: undefined,
                  }))
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="全部时间" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部时间</SelectItem>
                  <SelectItem value="week">最近一周</SelectItem>
                  <SelectItem value="month">最近一月</SelectItem>
                  <SelectItem value="year">最近一年</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Results / Recent Searches */}
      <div className="flex-1 overflow-auto">
        {debouncedQuery.length >= 2 ? (
          // Search Results
          <SearchResults
            results={results?.data ?? []}
            isLoading={isLoading}
            query={debouncedQuery}
            onSelect={handleSelectResult}
          />
        ) : (
          // Recent Searches
          <RecentSearches
            searches={recentSearches}
            onSelect={(search) => setQuery(search)}
            onClear={clearRecentSearches}
          />
        )}
      </div>
    </div>
  )
}

// ==================== Sub Components ====================

interface SearchResultsProps {
  results: SearchResult[]
  isLoading: boolean
  query: string
  onSelect: (result: SearchResult) => void
}

function SearchResults({ results, isLoading, query: _query, onSelect }: SearchResultsProps) {
  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ))}
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Search className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">未找到结果</p>
        <p className="text-sm mt-1">尝试使用不同的关键词搜索</p>
      </div>
    )
  }

  return (
    <div className="divide-y">
      <div className="px-4 py-2 text-sm text-muted-foreground">
        找到 {results.length} 个结果
      </div>
      {results.map((result) => (
        <SearchResultItem
          key={result.id}
          result={result}
          onClick={() => onSelect(result)}
        />
      ))}
    </div>
  )
}

interface SearchResultItemProps {
  result: SearchResult
  onClick: () => void
}

function SearchResultItem({ result, onClick }: SearchResultItemProps) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 hover:bg-muted/50 transition-colors"
    >
      <div className="flex items-start gap-3">
        <FileText className="h-5 w-5 mt-0.5 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div
            className="font-medium truncate"
            dangerouslySetInnerHTML={{ __html: result.highlightedTitle }}
          />
          <div
            className="text-sm text-muted-foreground mt-1 line-clamp-2"
            dangerouslySetInnerHTML={{ __html: result.highlightedExcerpt }}
          />
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" />
              {result.authorName}
            </span>
            <span className="inline-flex items-center gap-1">
              {result.knowledgeBaseType === 'company' ? (
                <Building2 className="h-3 w-3" />
              ) : (
                <FolderOpen className="h-3 w-3" />
              )}
              {result.knowledgeBaseName}
              {result.projectName && ` · ${result.projectName}`}
            </span>
            {result.updatedAt && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDate(result.updatedAt)}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

interface RecentSearchesProps {
  searches: string[]
  onSelect: (search: string) => void
  onClear: () => void
}

function RecentSearches({ searches, onSelect, onClear }: RecentSearchesProps) {
  if (searches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Clock className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">开始搜索</p>
        <p className="text-sm mt-1">输入关键词搜索文档</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between px-4 py-2">
        <span className="text-sm text-muted-foreground">最近搜索</span>
        <button
          onClick={onClear}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          清除
        </button>
      </div>
      <div className="divide-y">
        {searches.map((search, index) => (
          <button
            key={index}
            onClick={() => onSelect(search)}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
          >
            <Clock className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{search}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ==================== Helpers ====================

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric',
  })
}
